import { MediaCacheService } from '@src/services/MediaCacheService';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { sortByPlayheadProximity } from '@src/utility/timelineAssetUrls';

import type { TimelineAssetRef } from '@src/utility/timelineAssetUrls';

const logger = LogManager.getLogger('service:AssetPrewarmingService');

const ASSET_CACHE_CONCURRENCY = 4;

export type CacheAssetsSummary = {
  attempted: number;
  succeeded: number;
  failed: number;
  totalMs: number;
};

/**
 * Narrow surface the service needs from a renderer — keeps this module
 * decoupled from the concrete `Renderer` class. Any object with a
 * `cacheAsset(url, blob)` method qualifies, which makes mocking trivial
 * (no `as unknown as Renderer` casts in tests) and lets alternative
 * renderer implementations (test doubles, offscreen proxies) slot in.
 */
export interface RendererCacheTarget {
  cacheAsset(url: string, blob: Blob): Promise<void>;
  /** Set to true once the renderer's iframe has been torn down. When present
   *  and true, `cacheAsset` skips the push silently instead of propagating an
   *  error — the renderer is gone and nothing can be cached into it anyway. */
  isDisposed?: boolean;
}

/**
 * Pre-warms video/image/audio asset blobs into a Creatomate iframe's
 * CacheStorage via `RendererCacheTarget.cacheAsset(url, blob)`, so windowed
 * timelines can render rolled-in elements instantly instead of waiting on a
 * network fetch the moment the element enters the window.
 *
 * Owns its own dedup state (URLs already pushed per renderer + in-flight
 * blob fetches). The bridge is blob-typed, so the same code path works for
 * every media type — no per-type branches.
 *
 * Most callers should construct a `WarmedRenderer` and call
 * `renderer.warmAsset(url)` rather than calling this service directly —
 * that keeps the dispose / forget contract encapsulated in the renderer.
 */
export class AssetPrewarmingService {
  /** In-flight blob fetches keyed by URL — dedupes overlapping warms. */
  private readonly inflightFetches = new Map<string, Promise<Blob>>();
  /**
   * URLs already pushed into each renderer's CacheStorage. Per-renderer
   * because each Creatomate iframe has its own CacheStorage, so a URL
   * pushed into renderer A still needs to be pushed into renderer B.
   *
   * Entries are evicted explicitly via `forgetRenderer()` — a regular `Map`
   * (not `WeakMap`) so eviction is deterministic at dispose time rather
   * than tied to GC.
   */
  private readonly cachedUrlsByRenderer = new Map<
    RendererCacheTarget,
    Set<string>
  >();
  private readonly mediaCache: MediaCacheService;

  constructor(mediaCache: MediaCacheService = new MediaCacheService()) {
    this.mediaCache = mediaCache;
  }

  /**
   * Fetch the asset blob (deduped) and push it into the renderer.
   *
   * Per-renderer idempotent — if `url` has already been pushed to this
   * `renderer`, the call short-circuits without fetching or re-pushing.
   * Overlapping in-flight fetches for the same URL are deduped via
   * `inflightFetches`; blob reuse across calls is handled by
   * `MediaCacheService` (CacheStorage-backed).
   *
   * Throws on failure (MediaCacheService error or renderer push error).
   * Callers handle the rejection via try/catch or `.catch(...)` and decide
   * whether to log/retry.
   */
  async cacheAsset(renderer: RendererCacheTarget, url: string): Promise<void> {
    if (renderer.isDisposed) {
      logger.debug('[cacheAsset] Skipped — renderer is disposed', { url });
      return;
    }

    if (this.cachedUrlsByRenderer.get(renderer)?.has(url)) {
      // Observability: silent shortcircuits made dedup hard to verify in
      // the wild. Debug-level so production stays quiet.
      logger.debug('[cacheAsset] Skipped — already cached for this renderer', {
        url,
      });
      return;
    }

    const blob = await this._fetchAssetBlob(url);

    if (renderer.isDisposed) {
      logger.debug(
        '[cacheAsset] Skipped — renderer was disposed during blob fetch',
        { url },
      );
      return;
    }

    const cacheStart = performance.now();
    await renderer.cacheAsset(url, blob);
    const cacheMs = +(performance.now() - cacheStart).toFixed(2);

    let cached = this.cachedUrlsByRenderer.get(renderer);
    if (!cached) {
      cached = new Set<string>();
      this.cachedUrlsByRenderer.set(renderer, cached);
    }
    cached.add(url);

    logger.info('[cacheAsset] Asset cached in renderer', { url, cacheMs });
  }

  /**
   * Drop tracking state for a renderer that's about to be disposed.
   * `WarmedRenderer.dispose()` calls this automatically; non-WarmedRenderer
   * callers (rare) MUST invoke it adjacent to `renderer.dispose()` so the
   * service doesn't retain a reference to a dead iframe.
   */
  forgetRenderer(renderer: RendererCacheTarget): void {
    this.cachedUrlsByRenderer.delete(renderer);
  }

  /**
   * Bulk warm: sort by playhead proximity, then fan out with bounded
   * concurrency. Fail-soft per URL — individual failures are logged and
   * counted in the summary, but never propagate out. Resolves once all
   * URLs settle.
   */
  async cacheAssets(
    renderer: RendererCacheTarget,
    refs: TimelineAssetRef[],
    playheadSec: number,
  ): Promise<CacheAssetsSummary> {
    const pendingSorted = sortByPlayheadProximity(refs, playheadSec);

    logger.info('[cacheAssets] Warming assets', {
      total: pendingSorted.length,
      playheadSec,
    });

    const start = performance.now();
    let succeeded = 0;
    let failed = 0;
    let cursor = 0;

    const runNext = async (): Promise<void> => {
      while (cursor < pendingSorted.length) {
        const myIndex = cursor++;
        const ref = pendingSorted[myIndex];
        try {
          await this.cacheAsset(renderer, ref.url);
          succeeded++;
        } catch (error) {
          failed++;
          logger.warn('[cacheAssets] Failed to cache asset', {
            url: ref.url,
            ...extractErrorInfo(error),
          });
        }
      }
    };

    const workerCount = Math.min(ASSET_CACHE_CONCURRENCY, pendingSorted.length);
    const workers: Promise<void>[] = [];
    for (let i = 0; i < workerCount; i++) {
      workers.push(runNext());
    }
    await Promise.all(workers);

    const totalMs = +(performance.now() - start).toFixed(2);
    const summary: CacheAssetsSummary = {
      attempted: pendingSorted.length,
      succeeded,
      failed,
      totalMs,
    };
    logger.info('[cacheAssets] Bulk warm complete', summary);
    return summary;
  }

  private _fetchAssetBlob(url: string): Promise<Blob> {
    const existing = this.inflightFetches.get(url);
    if (existing) return existing;

    const promise = this._doFetchAssetBlob(url);
    this.inflightFetches.set(url, promise);
    // Detach cleanup chain — swallow rejection here so the in-flight map
    // entry is removed without producing an unhandled rejection. The
    // original `promise` is still returned to the caller, who handles
    // rejection on their own.
    void promise
      .catch(() => undefined)
      .finally(() => {
        this.inflightFetches.delete(url);
      });
    return promise;
  }

  private async _doFetchAssetBlob(url: string): Promise<Blob> {
    const downloadStart = performance.now();
    const result = await this.mediaCache.fetchMediaBlob(url);
    if (!result.ok) throw result.error;

    const { blob, fromCache } = result.value;
    const downloadMs = +(performance.now() - downloadStart).toFixed(2);
    logger.info(
      fromCache
        ? '[cacheAsset] Media loaded from CacheStorage'
        : '[cacheAsset] Media downloaded',
      { url, downloadMs },
    );

    return blob;
  }
}
